import api from '@/lib/axios';

import type {
  CountryDto,
  CreateTenantPayload,
  PaginatedResponse,
  ProductTypeCountryResolution,
  ProductTypeDto,
  TenantStatusCounts,
  TenantWithUser,
  UpdateTenantPayload,
} from '@/types/tenant';
import type { ListFilterPayload } from '@/types/filters';

// ============================================
// Tenants
// ============================================

export interface GetTenantsParams {
  page?: number;
  size?: number;
  sort?: string;
}

export const getTenants = async (
  params: GetTenantsParams = {},
  body?: ListFilterPayload,
): Promise<PaginatedResponse<TenantWithUser>> => {
  const response = await api.post<PaginatedResponse<TenantWithUser>>(
    '/platform-service/v1/tenants/list',
    body ?? {},
    { params },
  );
  return response.data;
};

export const getTenantById = async (id: string): Promise<TenantWithUser> => {
  const response = await api.get<TenantWithUser>(`/platform-service/v1/tenants/${id}`);
  return response.data;
};

export const getTenantBySubdomain = async (subdomain: string): Promise<TenantWithUser> => {
  const response = await api.get<TenantWithUser>(
    `/platform-service/v1/tenants/subdomain/${subdomain}`,
  );
  return response.data;
};

export const createTenant = async (data: CreateTenantPayload): Promise<TenantWithUser> => {
  const response = await api.post<TenantWithUser>('/platform-service/v1/tenants', data, {
    successToast: true,
  });
  return response.data;
};

export const updateTenant = async (
  id: string,
  data: UpdateTenantPayload,
): Promise<TenantWithUser> => {
  const response = await api.put<TenantWithUser>(`/platform-service/v1/tenants/${id}`, data, {
    successToast: true,
  });
  return response.data;
};

export const deleteTenant = async (id: string): Promise<void> => {
  await api.delete(`/platform-service/v1/tenants/${id}`, { successToast: true });
};

export const activateTenant = async (id: string): Promise<TenantWithUser> => {
  const response = await api.post<TenantWithUser>(
    `/platform-service/v1/tenants/${id}/activate`,
    undefined,
    { successToast: true },
  );
  return response.data;
};

export const requestTenantActivation = async (id: string): Promise<TenantWithUser> => {
  const response = await api.post<TenantWithUser>(
    `/platform-service/v1/tenants/${id}/request-activation`,
    undefined,
    { successToast: true },
  );
  return response.data;
};

export const requestTenantActivationSkipReview = async (id: string): Promise<TenantWithUser> => {
  const response = await api.post<TenantWithUser>(
    `/platform-service/v1/tenants/${id}/request-activation/skip-review`,
    undefined,
    { successToast: true },
  );
  return response.data;
};

export const getTenantCounts = async (): Promise<TenantStatusCounts> => {
  const response = await api.get<TenantStatusCounts>('/platform-service/v1/tenants/counts');
  return response.data;
};

export interface CheckSubdomainResponse {
  available: boolean;
  subdomain: string;
  messageCode: string | null;
}

// `available: false` is the single signal for both already-taken and reserved/blacklisted
// subdomains. Backend rolls reserved-name logic into this endpoint; messageCode is a K_ i18n key.
export const checkSubdomain = async (subdomain: string): Promise<CheckSubdomainResponse> => {
  const response = await api.get<CheckSubdomainResponse>(
    '/platform-service/v1/tenants/check-subdomain',
    {
      params: { subdomain },
    },
  );
  return response.data;
};

// ============================================
// Countries
// ============================================

export interface GetCountriesParams {
  search?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export const getCountries = async (
  params: GetCountriesParams = { size: 300 },
): Promise<PaginatedResponse<CountryDto>> => {
  const response = await api.get<PaginatedResponse<CountryDto>>('/platform-service/v1/countries', {
    params,
  });
  return response.data;
};

export const getCountryById = async (id: string): Promise<CountryDto> => {
  const response = await api.get<CountryDto>(`/platform-service/v1/countries/${id}`);
  return response.data;
};

/** Resolve available countries (with per-country employment types) per product type */
export const resolveCountries = async (
  productTypeIds: string[],
): Promise<ProductTypeCountryResolution[]> => {
  const response = await api.post<ProductTypeCountryResolution[]>(
    '/platform-service/v1/countries/resolve',
    { productTypeIds },
  );
  return response.data;
};

// ============================================
// Product Types
// ============================================

export interface GetProductTypesParams {
  search?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export const getProductTypes = async (
  params: GetProductTypesParams = { size: 100 },
  body?: ListFilterPayload,
): Promise<PaginatedResponse<ProductTypeDto>> => {
  const response = await api.post<PaginatedResponse<ProductTypeDto>>(
    '/platform-service/v1/product-types/list',
    body ?? {},
    { params },
  );
  return response.data;
};

export const getProductTypeById = async (id: string): Promise<ProductTypeDto> => {
  const response = await api.get<ProductTypeDto>(`/platform-service/v1/product-types/${id}`);
  return response.data;
};
